import type { IntegrityCompletionStatus } from '@really-assess/shared/integrations'
import type { ProctoringEvent } from '../types'

export interface IntegrityReportPayload {
  userId: string
  userName?: string
  userEmail?: string
  assessmentId: string
  assessmentSlug?: string
  sessionId?: string
  integrityScore: number
  completionStatus: IntegrityCompletionStatus
  timestamp: string
  violations: ProctoringEvent[]
  webhookUrl?: string
  webhookSecret?: string
}

export interface IntegrityReportResponse {
  success: boolean
  webhookDelivered?: boolean
  stored?: boolean
  error?: string
}

const FACE_API_URL = 'https://reallyassess-face-api-dev.techtree.workers.dev'

/**
 * Persist finalized integrity score on the Face API session record.
 */
export async function persistIntegrityToSession(
  sessionId: string,
  sessionToken: string,
  payload: Omit<IntegrityReportPayload, 'webhookUrl' | 'webhookSecret'>
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${FACE_API_URL}/api/sessions/${sessionId}/integrity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({
        integrityScore: payload.integrityScore,
        completionStatus: payload.completionStatus,
        timestamp: payload.timestamp,
        violations: payload.violations.map((v) => ({
          type: v.type,
          timestamp: v.timestamp,
          details: v.details,
          similarity: v.similarity,
        })),
        userId: payload.userId,
        userName: payload.userName,
        userEmail: payload.userEmail,
        assessmentId: payload.assessmentId,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Store failed' }))
      return { success: false, error: (errorData as { error?: string }).error }
    }

    return { success: true }
  } catch (error) {
    console.error('[IntegrityReport] Session store error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}

/**
 * Finalize integrity: persist to Face API session, then deliver webhook.
 */
export async function deliverIntegrityReport(
  payload: IntegrityReportPayload,
  session?: { id: string; token: string }
): Promise<IntegrityReportResponse> {
  let stored = false

  if (session?.id && session?.token) {
    const storeResult = await persistIntegrityToSession(session.id, session.token, payload)
    stored = storeResult.success
    if (!storeResult.success) {
      console.warn('[IntegrityReport] Failed to persist to session:', storeResult.error)
    }
  }

  try {
    const response = await fetch('/api/integrity/deliver', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Delivery failed' }))
      return {
        success: false,
        stored,
        error: (errorData as { error?: string }).error,
      }
    }

    const result = await response.json() as IntegrityReportResponse
    return { ...result, stored }
  } catch (error) {
    console.error('[IntegrityReport] Delivery error:', error)
    return {
      success: false,
      stored,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}
